import { Page } from '../components/Page'
import React, { useRef, useState } from 'react'
import { isEmpty } from 'lodash'
import {
  TextAnalysisResultType,
  analyzeText,
  textFromFile,
} from '../utils/resumeUtils'

const ResumeCard = ({
  setParsedText,
}: {
  setParsedText: (arg: string) => void
}) => {
  const fileInput = useRef(null)
  const [parsedText, setParsedTextInternal] = useState('')

  const handleFileUpload = async (e: any) => {
    const file = e.target.files[0] as File
    if (file) {
      const text = await textFromFile(file)
      console.log('Final text', text?.substring(0, 100))

      if (text) {
        setParsedTextInternal(text)
      }
    }
  }

  const handleAnalyze = () => {
    if (parsedText) {
      // Perform analysis here
      console.log('Analyze the parsed text')
      setParsedText(parsedText)
    }
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow-lg w-72">
      <h2 className="text-2xl font-semibold mb-4">Resume Analyzer</h2>
      <input
        type="file"
        className="mb-4 bg-gray-200 p-2 rounded w-full"
        accept=".txt,.pdf"
        onChange={handleFileUpload}
        ref={fileInput}
      />
      <label>Raw resume text:</label>
      <textarea
        className="w-full h-20 p-2 mb-4 border rounded"
        placeholder="Parsed text will appear here."
        readOnly
        value={parsedText}
      ></textarea>
      <button
        onClick={handleAnalyze}
        disabled={!parsedText}
        className={`w-full p-2 text-white ${
          parsedText ? 'bg-blue-500' : 'bg-blue-300 cursor-not-allowed'
        }`}
      >
        Analyze
      </button>
    </div>
  )
}

const AnalyzedResumeComponent = ({
  analyzedResults,
}: {
  analyzedResults: TextAnalysisResultType
}) => {
  if (
    analyzedResults.success === false &&
    analyzedResults.code === 'RATE_LIMIT'
  ) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-lg w-full">
        <h2 className="text-2xl font-semibold mb-4">Analyzed Resume</h2>
        <p>
          Hey, you hit your limit for the day! OpenAI credits are not free 😥
        </p>
        <p>
          Are you enjoying this tool? If you are,{' '}
          <a
            href="https://forms.gle/eh53bF9yHKRb1VdG7"
            target="_blank"
            rel="noreferrer"
            className="text-blue-600 hover:text-blue-800 underline transition duration-300 ease-in-out"
          >
            please sign up for the beta user list for the paid version of this
            product here.
          </a>
        </p>
      </div>
    )
  }

  if (analyzedResults.success === false) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-lg w-full">
        <h2 className="text-2xl font-semibold mb-4">Analyzed Resume</h2>
        <p>
          Uh, something went wrong! Please try a different resume or try again
          later.
        </p>
      </div>
    )
  }

  const data = analyzedResults.data

  return (
    <div className="bg-white p-6 rounded-lg shadow-lg w-full">
      <h2 className="text-2xl font-semibold mb-4">Analyzed Resume</h2>
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-1">
          <h3 className="text-xl font-semibold mb-2">Personal Info</h3>
          <p>
            <strong>Name:</strong> {data.name}
          </p>
          <p>
            <strong>Email:</strong> {data.email ? data.email : 'N/A'}
          </p>
          <p>
            <strong>Phone:</strong> {data.phone ? data.phone : 'N/A'}
          </p>
          <p>
            <strong>Location:</strong> {data.location ? data.location : 'N/A'}
          </p>
          {data.linkedin && (
            <p>
              <strong>LinkedIn:</strong>{' '}
              <a
                href={data.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline"
              >
                LinkedIn Profile
              </a>
            </p>
          )}
          {data.github && (
            <p>
              <strong>GitHub:</strong>{' '}
              <a
                href={data.github}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline"
              >
                GitHub Profile
              </a>
            </p>
          )}
          {data.website && (
            <p>
              <strong>Website:</strong>{' '}
              <a
                href={data.website}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline"
              >
                Personal Website
              </a>
            </p>
          )}
        </div>
        <div className="col-span-1">
          <h3 className="text-xl font-semibold mb-2">Professional Info</h3>
          <p>
            <strong>Years of Experience:</strong> {data.yearsOfExperienceTotal}
          </p>
          <p>
            <strong>Primary Focus:</strong> {data.primaryFocus}
          </p>
          <p>
            <strong>Programming Languages:</strong> {data.languages.join(', ')}
          </p>
          <p>
            <strong>Most Repeated Technologies:</strong>{' '}
            {isEmpty(data.mostRepeatedTechnologies)
              ? 'N/A'
              : data.mostRepeatedTechnologies.join(', ')}
          </p>
          <p>
            <strong>Attended Bootcamp:</strong>{' '}
            {data.attendedBootcamp ? 'Yes' : 'No'}
          </p>
          <p>
            <strong>Rating:</strong> {data.rating} - {data.ratingReason}
          </p>
        </div>
      </div>

      {/* Jobs are shown as cards in a list */}
      <h3 className="text-xl font-semibold mt-4 mb-2">Jobs</h3>
      <ul className="space-y-4">
        {data.jobs.map((job, index) => (
          <li key={index} className="bg-gray-100 p-4 rounded-md shadow">
            <div className="text-lg font-semibold">{job.jobtitle}</div>
            {job.company && (
              <p className="mt-2 text-sm text-gray-600">
                <strong>Company:</strong> {job.company}
              </p>
            )}
            {job.startDate || job.endDate ? (
              <p className="mt-2 text-sm text-gray-600">
                <strong>Duration:</strong>{' '}
                {job.startDate ? job.startDate : 'Unknown'} -{' '}
                {job.endDate ? job.endDate : 'Current'}
              </p>
            ) : null}
            {job.skillsMentioned && job.skillsMentioned.length > 0 && (
              <p className="mt-2 text-sm text-gray-600">
                <strong>Skills Mentioned:</strong>{' '}
                {job.skillsMentioned.join(', ')}
              </p>
            )}
            {job.technologiesUsed && job.technologiesUsed.length > 0 && (
              <p className="mt-2 text-sm text-gray-600">
                <strong>Technologies Used:</strong>{' '}
                {job.technologiesUsed.join(', ')}
              </p>
            )}
            {job.jobType && (
              <p className="mt-2 text-sm text-gray-600">
                <strong>Job Type:</strong> {job.jobType}
              </p>
            )}
          </li>
        ))}
      </ul>

      <h3 className="text-xl font-semibold mt-4 mb-2">Projects</h3>
      <ul>
        {data.projects.map((project, index) => (
          <li key={index} className="mb-2">
            {project}
          </li>
        ))}
      </ul>

      {/* Education and Certifications */}
      <div className="grid grid-cols-2 gap-4 mt-4">
        <div className="col-span-1">
          <h3 className="text-xl font-semibold mb-2">Education</h3>
          <p>
            <strong>Degree:</strong> {data.education.degree}
          </p>
          <p>
            <strong>Institution:</strong> {data.education.institution}
          </p>
          <p>
            <strong>Dates:</strong> {data.education.dates}
          </p>
          <p>
            <strong>STEM:</strong> {data.education.isStem ? 'Yes' : 'No'}
          </p>
        </div>
        <div className="col-span-1">
          <h3 className="text-xl font-semibold mb-2">Certifications</h3>
          <ul>
            {data.certifications.map((cert, index) => (
              <li key={index} className="mb-2">
                {cert}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  )
}

const LoadingSpinner = () => (
  <div className="flex flex-col justify-center items-center p-6">
    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-purple-500 mb-4"></div>
    <p className="text-center text-sm text-gray-500">
      Analyzing... this may take several seconds
    </p>
  </div>
)

export const DevResumeAnalyzer = () => {
  const [analyzedResults, setAnalyzedResults] = useState<
    TextAnalysisResultType[]
  >([])

  const [analysisMap, setAnalysisMap] = useState<{ [key: string]: any }>({})
  const [isAnalyzing, setIsAnalyzing] = useState<boolean>(false)

  const handleAnalyze = async (parsedText: string) => {
    console.log(
      'Running analyze',
      !!parsedText,
      isAnalyzing,
      analysisMap,
      parsedText.length,
    )
    /* 
        Is it parsed, is it not analyzing, and is it not in the analysis map?
    */
    if (parsedText && !isAnalyzing && !analysisMap[parsedText]) {
      setIsAnalyzing(true)

      const analysis = await analyzeText(parsedText)

      setAnalysisMap({ ...analysisMap, [parsedText]: analysis })
      setAnalyzedResults([...analyzedResults, analysis])
      setIsAnalyzing(false)
    }
  }

  return (
    <Page>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="md:col-span-1">
          <ResumeCard
            setParsedText={(parsedText: string) => {
              console.log('Calling setParsedText', parsedText.substring(0, 100))
              handleAnalyze(parsedText)
            }}
          />
        </div>
        <div className="md:col-span-3">
          <div>{isAnalyzing ? <LoadingSpinner /> : null}</div>
          <div>
            {analyzedResults.map((result, index) => {
              return (
                <AnalyzedResumeComponent analyzedResults={result} key={index} />
              )
            })}
          </div>
        </div>
      </div>
    </Page>
  )
}
